/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { Chart } from 'react-google-charts';

import {
  format,
  parseISO,
  addMonths,
  eachMonthOfInterval,
  addYears,
} from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import {
  TIPO_USUARIO,
  arrayGroupBy,
  formatPrice,
} from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useClinica } from '../../../hooks/clinica';
import { useAuth } from '../../../hooks/auth';

import Input from '../../../components/Input';
import SelectSync from '../../../components/SelectSync';
import Loading from '../../../components/Loading';

import getValidationErrors from '../../../utils/getValidationErrors';

import {
  RelatorioHeader,
  ContainerResumo,
  ContainerAssociados,
} from './styles';
import getResponseErrors from '../../../utils/getResponseErrors';
import PageInfo from '../../../components/PageInfo';

interface IRetornoDescricaoTotal {
  descricao: string;
  total: number;
}

interface IRetornoDescricaoTotalClinica extends IRetornoDescricaoTotal {
  CliNome: string;
  CliCodigo: number;
}

interface IRetornoEstatisticas {
  diasEscolhidos: IRetornoDescricaoTotal[];
  faixaEtaria: IRetornoDescricaoTotal[];
  motivoCancelar: IRetornoDescricaoTotal[];
  motivoNaoRenovar: IRetornoDescricaoTotal[];
  situacao: IRetornoDescricaoTotalClinica[];
  quantidadeAssociados: IRetornoDescricaoTotalClinica[];
}

interface IAssociados {
  clinica: string;
  totalGeral: number;
  dados: IRetornoDescricaoTotalClinica[];
}

interface IGenericType {
  [key: string]: any;
}

interface IResumoSituacao {
  clinica: string;
  cartoes: number;
  ativos: number;
  atrasados?: number;
  expirados: number;
  linkExpirados: string;
  cancelados: number;
  linkCancelados: string;
}

interface IFormBuscaPerformance {
  dataInicio: Date;
  dataFim: Date;
  CliCodigo: string;
}

interface IFormBuscaAtividade {
  dataInicio: Date;
}

interface IPerformanceCartao {
  CliCodigo: number;
  CliNome: string;
  mes: string;
  mesOrdena: string;
  total: number;
  situacao?: string;
}
interface IRetornoPerformanceCartao {
  novos: IPerformanceCartao[];
  cancelados: IPerformanceCartao[];
  expirados: IPerformanceCartao[];
}

interface IPerformancePorClinica {
  CliCodigo: number;
  CliNome: string;
  novos: number;
  cancelados: number;
  expirados: number;
}

interface IRetornoAtividades {
  CliNome: string;
  CliCodigo: number;
  descricao: string;
  total: number;
  ComOrigem: Number;
}

interface IRetornoCartaoNovo {
  CliCodigo: number;
  CliNome: string;
  PlaCodigo: number;
  PlaNumero: string;
  PlaValor: number;
  PlaValorFormatado: string;
  CprValorPag: number;
  CprValorPagFormatado: string;
  CprPago: boolean;
  PacNome: string;
}

const CartaoSaude: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsClinica, imagensClinica } = useClinica();

  const logo =
    imagensClinica.find(img => img.nome === 'logoRelatorio')?.arquivo_url || '';

  const formRefClinica = useRef<FormHandles>(null);
  const formRefPerformance = useRef<FormHandles>(null);
  const formRefAtividade = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dataGerencial, setDataGerencial] = useState('Todo');

  const initialData = {
    CliCodigo: user.UsuClinica.toString(),
  };

  const initialDataPerformance = {
    dataInicio: format(addMonths(new Date(), -6), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
  };

  const initialDataAtividade = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
  };

  const [clinicaSelecionada, setClinicaSelecionada] = useState<number>();

  const [graficoDias, setGraficoDias] = useState<IGenericType[]>([
    ['Dia', 'Total'],
  ]);
  const [graficoFaixa, setGraficoFaixa] = useState<IGenericType[]>([
    ['Faixa', 'Total'],
  ]);
  const [graficoSituacao, setGraficoSituacao] = useState<IGenericType[]>([
    ['Situação', 'Total'],
  ]);
  const [graficoMotivoCancelar, setGraficoMotivoCancelar] = useState<
    IGenericType[]
  >([['Motivo', 'Total']]);
  const [graficoMotivoNaoRenovar, setGraficoMotivoNaoRenovar] = useState<
    IGenericType[]
  >([['Motivo', 'Total']]);

  const [graficoPerformance, setGraficoPerformance] = useState<IGenericType[]>([
    ['Mes', 'Novos', 'Cancelados', 'Expirados'],
  ]);

  const [performancePorClinica, setPerformancePorClinica] = useState<
    IPerformancePorClinica[]
  >();

  const [resumoSituacao, setResumoSituacao] = useState<IResumoSituacao[]>();

  const [qtdAssociados, setQtdAssociados] = useState<IAssociados[]>();

  const [atividades, setAtividades] = useState<IRetornoAtividades[][]>();
  const [cartoesNovosDia, setCartoesNovosDia] = useState<
    IRetornoCartaoNovo[][]
  >();

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const informacoesPagina = `<p><b>Descrição:</b> Resumo com os principais indicadores do Cartão Saúde.</p><p><b>Obs.</b> Quando selecionado um período leva em consideração a data de início do cartão dentro daquele período.</p>`;

  useEffect(() => {
    formRefClinica.current?.submitForm();
    formRefPerformance.current?.submitForm();
    formRefAtividade.current?.submitForm();
  }, [clinicaSelecionada]);

  const handleSubmitGeral = useCallback(
    async (dados: {
      dataInicio?: Date;
      dataFim?: Date;
      CliCodigo?: string;
    }) => {
      setLoading(true);
      try {
        formRefClinica.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date()
            .nullable()
            .transform((curr, orig) => (orig === '' ? null : curr)),
          dataFim: Yup.date()
            .nullable()
            .transform((curr, orig) => (orig === '' ? null : curr)),
          CliCodigo: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const parametroClinica = dados.CliCodigo
          ? `?CliCodigo=${dados.CliCodigo}`
          : '';

        let parametroPeriodo = '';
        if (
          dados.dataInicio &&
          dados.dataInicio.toString() !== '' &&
          dados.dataFim &&
          dados.dataFim.toString() !== ''
        ) {
          const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
          const dataFimSelecionada = parseISO(dados.dataFim.toString());

          const dataInicioFormatada = format(
            dataInicioSelecionada,
            'yyyy-MM-dd',
          );
          const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');
          parametroPeriodo = `&dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}`;
          setDataGerencial(
            `de ${format(dataInicioSelecionada, 'dd/MM/yyyy')} até ${format(
              dataFimSelecionada,
              'dd/MM/yyyy',
            )}`,
          );
        } else {
          setDataGerencial('Todo');
        }

        setClinicaSelecionada(Number(dados.CliCodigo) || undefined);

        const response = await api.get<IRetornoEstatisticas>(
          `/relatorios/estatisticascartao${parametroClinica}${parametroPeriodo}`,
        );

        const graficoDiaLegenda: IGenericType[] = [['Dia', 'Total']];

        const diasEscolhidos: IGenericType[] = response.data.diasEscolhidos.map(
          rel => {
            return [rel.descricao, rel.total];
          },
        );

        setGraficoDias(graficoDiaLegenda.concat(diasEscolhidos));

        const graficoFaixaLegenda: IGenericType[] = [['Faixa', 'Total']];

        const faixaEtaria: IGenericType[] = response.data.faixaEtaria.map(
          rel => {
            return [rel.descricao, rel.total];
          },
        );

        setGraficoFaixa(graficoFaixaLegenda.concat(faixaEtaria));

        const graficoSituacaoLegenda: IGenericType[] = [['Situação', 'Total']];

        const totalEmDia = response.data.situacao
          .filter(rel => rel.descricao === 'Em Dia')
          .reduce((tot, item) => {
            return tot + Number(item.total || 0);
          }, 0);

        const totalCancelado = response.data.situacao
          .filter(rel => rel.descricao === 'Cancelado')
          .reduce((tot, item) => {
            return tot + Number(item.total || 0);
          }, 0);

        const totalExpirado = response.data.situacao
          .filter(rel => rel.descricao === 'Expirado')
          .reduce((tot, item) => {
            return tot + Number(item.total || 0);
          }, 0);

        const totalAtrasado = response.data.situacao
          .filter(rel => rel.descricao === 'Atrasado')
          .reduce((tot, item) => {
            return tot + Number(item.total || 0);
          }, 0);

        const situacao: IGenericType[] = [
          [parametroPeriodo === '' ? 'Em Dia' : 'Novos', totalEmDia],
          ['Cancelado', totalCancelado],
          ['Expirado', totalExpirado],
        ];

        const situacaoComAtrasados =
          parametroPeriodo === '' ? ['Atrasado', totalAtrasado] : [];

        setGraficoSituacao(
          situacaoComAtrasados.length > 0
            ? graficoSituacaoLegenda
                .concat(situacao)
                .concat([situacaoComAtrasados])
            : graficoSituacaoLegenda.concat(situacao),
        );

        const situacaoOrdenado = arrayGroupBy(
          response.data.situacao,
          i => i.CliNome,
        );

        const linkListagem = `cartaosaudelistagem?dataInicio=${format(
          addYears(new Date(), -6),
          'yyyy-MM-dd',
        )}&dataFim=${format(new Date(), 'yyyy-MM-dd')}`;

        const resumoSituacaoFormatado: IResumoSituacao[] = situacaoOrdenado.map(
          rel => {
            return {
              clinica: rel[0].CliNome,
              cartoes: rel.reduce((tot, item) => {
                return tot + Number(item.total || 0);
              }, 0),
              ativos: rel.find(item => item.descricao === 'Em Dia')?.total || 0,
              atrasados:
                rel.find(item => item.descricao === 'Atrasado')?.total || 0,
              expirados:
                rel.find(item => item.descricao === 'Expirado')?.total || 0,
              linkExpirados: `${linkListagem}&situacao=expirados`,
              cancelados:
                rel.find(item => item.descricao === 'Cancelado')?.total || 0,
              linkCancelados: `${linkListagem}&situacao=cancelados`,
            };
          },
        );

        if (situacaoOrdenado.length > 1)
          resumoSituacaoFormatado.push({
            clinica: 'TOTAL GERAL',
            cartoes:
              totalEmDia +
              totalCancelado +
              totalExpirado +
              (parametroPeriodo === '' ? totalAtrasado : 0),
            ativos: totalEmDia,
            ...(parametroPeriodo === '' ? { atrasados: totalAtrasado } : {}),
            linkCancelados: `${linkListagem}&situacao=cancelados`,
            linkExpirados: `${linkListagem}&situacao=expirados`,
            expirados: totalExpirado,
            cancelados: totalCancelado,
          });

        setResumoSituacao(resumoSituacaoFormatado);

        const graficoCancelarLegenda: IGenericType[] = [['Motivo', 'Total']];

        const motivoCancelar: IGenericType[] = response.data.motivoCancelar.map(
          rel => {
            return [rel.descricao, rel.total];
          },
        );

        setGraficoMotivoCancelar(graficoCancelarLegenda.concat(motivoCancelar));

        const graficoNaoRenovarLegenda: IGenericType[] = [['Motivo', 'Total']];

        const motivoNaoRenovar: IGenericType[] = response.data.motivoNaoRenovar.map(
          rel => {
            return [rel.descricao, rel.total];
          },
        );

        setGraficoMotivoNaoRenovar(
          graficoNaoRenovarLegenda.concat(motivoNaoRenovar),
        );

        const associadosAgrupado = arrayGroupBy(
          response.data.quantidadeAssociados,
          i => i.CliNome,
        );

        const associados = associadosAgrupado.map(rel => {
          return {
            clinica: rel[0].CliNome,
            totalGeral: rel.reduce((tot, item) => {
              return tot + Number(item.descricao) * Number(item.total || 0);
            }, 0),
            dados: rel,
          };
        });

        setQtdAssociados(associados);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefClinica.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleSubmitPerformance = useCallback(
    async (dados: IFormBuscaPerformance) => {
      setLoading(true);
      try {
        formRefPerformance.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        const parametrosOpcionais = clinicaSelecionada
          ? `&CliCodigo=${clinicaSelecionada}`
          : '';

        const response = await api.get<IRetornoPerformanceCartao>(
          `/relatorios/performancecartao?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}${parametrosOpcionais}`,
        );

        const meses = eachMonthOfInterval({
          start: dataInicioSelecionada,
          end: dataFimSelecionada,
        }).map(data => format(data, 'MM-yyyy'));

        const resultado: IGenericType[] = [];

        meses.forEach(mes => {
          const novosFiltrado = response.data.novos.filter(
            val => val.mes === mes,
          );
          const canceladosFiltrado = response.data.cancelados.filter(
            val => val.mes === mes,
          );
          const expiradosFiltrado = response.data.expirados.filter(
            val => val.mes === mes,
          );
          const novos = novosFiltrado.reduce((tot, item) => {
            return tot + Number(item.total || 0);
          }, 0);
          const cancelados = canceladosFiltrado.reduce((tot, item) => {
            return tot + Number(item.total || 0);
          }, 0);
          const expirados = expiradosFiltrado.reduce((tot, item) => {
            return tot + Number(item.total || 0);
          }, 0);
          resultado.push([mes, novos, cancelados, expirados]);
          // receitaDespesa.push([mes, consulta + cartaoSaude, despesas]);
        });

        const graficoPerformanceLegenda: IGenericType[] = [
          ['Mes', 'Novos', 'Cancelados', 'Expirados'],
        ];

        setGraficoPerformance(graficoPerformanceLegenda.concat(resultado));

        const performanceClinica: IPerformancePorClinica[] = [];
        optionsClinica.forEach(cli => {
          const novos = response.data.novos
            .filter(val => val.CliCodigo === Number(cli.value))
            .reduce((tot, item) => {
              return tot + Number(item.total || 0);
            }, 0);
          const cancelados = response.data.cancelados
            .filter(val => val.CliCodigo === Number(cli.value))
            .reduce((tot, item) => {
              return tot + Number(item.total || 0);
            }, 0);
          const expirados = response.data.expirados
            .filter(val => val.CliCodigo === Number(cli.value))
            .reduce((tot, item) => {
              return tot + Number(item.total || 0);
            }, 0);

          if (clinicaSelecionada) {
            if (clinicaSelecionada === Number(cli.value))
              performanceClinica.push({
                CliCodigo: Number(cli.value),
                CliNome: cli.label,
                novos,
                cancelados,
                expirados,
              });
          } else {
            performanceClinica.push({
              CliCodigo: Number(cli.value),
              CliNome: cli.label,
              novos,
              cancelados,
              expirados,
            });
          }
        });

        setPerformancePorClinica(performanceClinica);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefPerformance.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, clinicaSelecionada, optionsClinica],
  );

  const handleSubmitAtividade = useCallback(
    async (dados: IFormBuscaAtividade) => {
      setLoading(true);
      try {
        formRefAtividade.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = dataInicioSelecionada;

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        const parametrosOpcionais = clinicaSelecionada
          ? `&CliCodigo=${clinicaSelecionada}`
          : '';

        const response = await api.get<{
          atividades: IRetornoAtividades[];
          cartoesNovos: IRetornoCartaoNovo[];
        }>(
          `/relatorios/atividadescartao?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}${parametrosOpcionais}`,
        );

        const atividadesFormatado = response.data.atividades.map(rel => {
          let descricao = '';

          switch (rel.descricao) {
            case 'Novo Cartão':
              descricao = `<a href='cartaosaudelistagem?data=${dataInicioFormatada}&situacao=novos'>Novo Cartão</a>`;
              break;

            case 'Cancelamento':
              descricao = `<a href='cartaosaudelistagem?data=${dataInicioFormatada}&situacao=cancelados'>Cancelamento</a>`;
              break;

            default:
              descricao = rel.descricao;
          }

          return {
            ...rel,
            descricao,
          };
        });

        const dadosAtividadesAgrupado = arrayGroupBy(
          atividadesFormatado,
          i => i.CliNome,
        );

        setAtividades(dadosAtividadesAgrupado);

        const novos = response.data.cartoesNovos.map(rel => {
          return {
            ...rel,
            PlaValorFormatado: formatPrice(rel.PlaValor),
            CprValorPagFormatado: rel.CprPago
              ? formatPrice(rel.CprValorPag)
              : 'Não',
          };
        });

        const dadosNovosAgrupado = arrayGroupBy(novos, i => i.CliNome);

        setCartoesNovosDia(dadosNovosAgrupado);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefAtividade.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, clinicaSelecionada],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);
  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}

      <div className="bg-white border p-2 my-3 d-flex justify-content-between align-items-center">
        <img src={logo} alt="Agenda Facil" />{' '}
        <h2 id="topoBusca">Resumo Cartão Saúde</h2>
        <div className="d-flex justify-content-between align-items-center">
          <span className="pr-2">
            Impresso em {format(new Date(), 'dd/MM/yyyy')}
          </span>
          <PageInfo
            descricao={informacoesPagina}
            permissao={TIPO_USUARIO.SUPERVISOR}
          />
        </div>
      </div>

      <div className="bg-white border p-2 my-1">
        {optionsClinica.length > 0 && user && (
          <Form
            ref={formRefClinica}
            initialData={initialData}
            onSubmit={handleSubmitGeral}
            className="mb-2 d-print-none"
          >
            <Row>
              <Col md={3}>
                <Input name="dataInicio" label="De" type="date" />
              </Col>
              <Col md={3}>
                <Input name="dataFim" label="Até" type="date" />
              </Col>
              <Col md={4}>
                <SelectSync
                  name="CliCodigo"
                  label="Clinica"
                  isDisabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
                  isClearable={user.UsuTipo === TIPO_USUARIO.ADMINISTRADOR}
                  options={optionsClinica}
                  handleSelectChange={handleOnChangeVazio}
                />
              </Col>
              <Col md={2}>
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </div>

      <RelatorioHeader>
        Resumo Dias, Faixa Etária e Situação - Período {dataGerencial}
      </RelatorioHeader>
      <div className="bg-white border p-2">
        <div className="d-flex justify-content-between">
          <div style={{ width: '50%' }}>
            <Chart
              chartType="PieChart"
              data={graficoDias}
              options={{
                title: `Vencimentos Escolhidos`,
                is3D: true,
              }}
              loader={<div>carregando...</div>}
              width="100%"
              height="300px"
              legendToggle
            />
          </div>
          <div style={{ width: '50%' }}>
            <Chart
              chartType="PieChart"
              data={graficoFaixa}
              options={{
                title: `Faixa Etaria`,
                is3D: true,
              }}
              width="100%"
              height="300px"
              legendToggle
            />
          </div>
          <div style={{ width: '50%' }}>
            <Chart
              chartType="PieChart"
              data={graficoSituacao}
              options={{
                title: `Situação`,
                is3D: true,
              }}
              width="100%"
              height="300px"
              legendToggle
            />
          </div>
        </div>
      </div>

      <RelatorioHeader className="mt-2">
        Quantidade de Cartões por Situação - Período {dataGerencial}
      </RelatorioHeader>
      <div className="bg-white border p-2 mb-2">
        {resumoSituacao &&
          resumoSituacao.map(res => (
            <React.Fragment key={`res-${res.clinica}`}>
              <h6 className="text-center">{res.clinica}</h6>
              <div className="card-deck">
                <div className="card">
                  <div className="card-body py-2 bg-info text-center text-light">
                    <h5>{res.cartoes}</h5> CARTÕES
                  </div>
                </div>
                <div className="card">
                  <div className="card-body py-2 bg-info text-center text-light">
                    <h5>{res.ativos}</h5>{' '}
                    {`${dataGerencial !== 'Todo' ? 'NOVOS' : 'ATIVOS'}`}
                  </div>
                </div>
                <div
                  className={`card ${dataGerencial !== 'Todo' ? 'd-none' : ''}`}
                >
                  <div className="card-body py-2 bg-danger text-center text-light">
                    <h5>{res.atrasados}</h5> ATRASADOS
                  </div>
                </div>

                <div className="card">
                  <div className="card-body py-2 bg-info text-center text-light">
                    <a href={res.linkExpirados} className="text-white">
                      <h5>{res.expirados}</h5> EXPIRADOS
                    </a>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body py-2 bg-info text-center text-light">
                    <a href={res.linkCancelados} className="text-white">
                      <h5>{res.cancelados}</h5> CANCELADOS
                    </a>
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}
      </div>

      <RelatorioHeader>
        Motivos Cancelamento / Não Renovação - Período {dataGerencial}
      </RelatorioHeader>
      <div className="bg-white border p-2">
        <div className="d-flex justify-content-between">
          <div style={{ width: '50%' }}>
            <Chart
              chartType="PieChart"
              data={graficoMotivoCancelar}
              options={{
                title: `Motivos Cancelamento`,
                pieHole: 0.2,
              }}
              width="100%"
              height="300px"
              legendToggle
            />
          </div>
          <div style={{ width: '50%' }}>
            <Chart
              chartType="PieChart"
              data={graficoMotivoNaoRenovar}
              options={{
                title: `Motivos Não Renovar`,
                pieHole: 0.2,
              }}
              width="100%"
              height="300px"
              legendToggle
            />
          </div>
        </div>
      </div>

      <RelatorioHeader className="mt-2">
        Quantidade de Associados Por Cartão - Período {dataGerencial}
      </RelatorioHeader>
      <ContainerAssociados>
        {qtdAssociados &&
          qtdAssociados.map(rel => (
            <ContainerResumo key={`Assoc-${rel.dados[0].CliCodigo}`}>
              <h5>{rel.clinica}</h5>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th>Associados</th>
                    <th style={{ width: '95px' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rel.dados.map(res => (
                    <tr key={`qtdDep-${res.descricao}`}>
                      <td>{res.descricao}</td>
                      <td style={{ textAlign: 'right' }}>{res.total}</td>
                    </tr>
                  ))}
                  <tr>
                    <td>
                      <b>Total geral</b>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <b>{rel.totalGeral}</b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </ContainerResumo>
          ))}
      </ContainerAssociados>

      <RelatorioHeader className="mt-2">Performance</RelatorioHeader>
      <div className="bg-white border p-2">
        <div className="d-print-none">
          <Form
            ref={formRefPerformance}
            initialData={initialDataPerformance}
            onSubmit={handleSubmitPerformance}
          >
            <Row>
              <Col md={3}>
                <Input name="dataInicio" label="De" type="date" required />
              </Col>
              <Col md={3}>
                <Input name="dataFim" label="Até" type="date" required />
              </Col>
              <Col md={2}>
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
        <Row>
          <Col>
            <Chart
              chartType="AreaChart"
              data={graficoPerformance}
              options={{
                title: `Performance Cartões`,
                legend: { position: 'bottom' },
                vAxis: { title: 'Cartões' },
                hAxis: { title: 'Mês' },
              }}
              width="100%"
              height="300px"
              legendToggle
            />
          </Col>
          <Col>
            {performancePorClinica &&
              performancePorClinica.map(rel => (
                <ContainerResumo key={`perfor-${rel.CliCodigo}`}>
                  <h5>{rel.CliNome}</h5>
                  <table
                    className="table table-hover table-sm table-font-small table-bordered"
                    style={{ borderWidth: '0px' }}
                  >
                    <thead>
                      <tr className="text-center">
                        <th>Cartões</th>
                        <th style={{ width: '95px' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Novos</td>
                        <td style={{ textAlign: 'right' }}>{rel.novos}</td>
                      </tr>
                      <tr>
                        <td>Cancelados</td>
                        <td style={{ textAlign: 'right' }}>{rel.cancelados}</td>
                      </tr>
                      <tr>
                        <td>Expirados</td>
                        <td style={{ textAlign: 'right' }}>{rel.expirados}</td>
                      </tr>
                    </tbody>
                  </table>
                </ContainerResumo>
              ))}
          </Col>
        </Row>
      </div>

      <RelatorioHeader className="mt-2">
        Resumo de Atividades e Novos Cartões
      </RelatorioHeader>
      <div className="bg-white border p-2">
        <div className="d-print-none">
          <Form
            ref={formRefAtividade}
            initialData={initialDataAtividade}
            onSubmit={handleSubmitAtividade}
          >
            <Row>
              <Col md={3}>
                <Input name="dataInicio" label="Data" type="date" required />
              </Col>
              <Col md={2}>
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        {atividades && atividades.length > 0 ? (
          <>
            <h4>Atividades</h4>
            <div className="d-flex justify-content-between">
              {atividades.map(rel => (
                <ContainerResumo key={`ativ-${rel[0].CliCodigo}`}>
                  <h5>{rel[0].CliNome}</h5>
                  <table
                    className="table table-hover table-sm table-font-small table-bordered"
                    style={{ borderWidth: '0px' }}
                  >
                    <thead>
                      <tr className="text-center">
                        <th>Tipo</th>
                        <th style={{ width: '95px' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rel.map(res => (
                        <tr key={`ativdados-${res.CliCodigo}${res.descricao}`}>
                          <td>
                            <span
                              dangerouslySetInnerHTML={{
                                __html: res.descricao,
                              }}
                            />
                          </td>
                          <td style={{ textAlign: 'right' }}>{res.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ContainerResumo>
              ))}
            </div>
          </>
        ) : (
          <p style={{ textAlign: 'center' }}>Nenhum registro de atividades</p>
        )}

        <div className="w-100 mt-2" />

        {cartoesNovosDia && cartoesNovosDia.length > 0 ? (
          <>
            <h4>Cartões Novos</h4>
            <div className="d-flex justify-content-between">
              {cartoesNovosDia.map(rel => (
                <ContainerResumo
                  key={`novos-${rel[0].CliCodigo}`}
                  style={{ maxWidth: '360px' }}
                >
                  <h5>{rel[0].CliNome}</h5>
                  <table
                    className="table table-hover table-sm table-font-small table-bordered"
                    style={{ borderWidth: '0px' }}
                  >
                    <thead>
                      <tr className="text-center">
                        <th>Nº Cartão - Titular</th>
                        <th style={{ width: '63px' }}>Valor</th>
                        <th style={{ width: '63px' }}>Pago</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rel.map(res => (
                        <tr
                          key={`novosdados-${res.PlaCodigo}`}
                          style={{ fontSize: '.7rem' }}
                        >
                          <td>
                            {res.PlaNumero} - {res.PacNome}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {res.PlaValorFormatado}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {res.CprValorPagFormatado}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ContainerResumo>
              ))}
            </div>
          </>
        ) : (
          <p style={{ textAlign: 'center' }}>Nenhum cartão novo na data</p>
        )}
      </div>
    </Container>
  );
};

export default CartaoSaude;
